/**
 * Suggests a corrected email when the domain looks like a common typo
 * (e.g. gmail.con → gmail.com). Returns null when no suggestion applies.
 */

const COMMON_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'hotmail.com',
  'hotmail.com.ar',
  'yahoo.com',
  'yahoo.com.ar',
  'outlook.com',
  'outlook.es',
  'live.com',
  'icloud.com',
  'me.com',
  'msn.com',
  'mail.com',
  'protonmail.com',
  'proton.me',
  'aol.com',
];

const levenshtein = (a: string, b: string): number => {
  if (a === b) {
    return 0;
  }
  if (!a.length) {
    return b.length;
  }
  if (!b.length) {
    return a.length;
  }

  const prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  const curr = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,
        curr[j - 1] + 1,
        prev[j - 1] + cost,
      );
    }
    for (let j = 0; j <= b.length; j += 1) {
      prev[j] = curr[j];
    }
  }

  return prev[b.length];
};

const suggestDomain = (domain: string): string | null => {
  if (COMMON_DOMAINS.includes(domain)) {
    return null;
  }

  let best: string | null = null;
  let bestDistance = Infinity;

  COMMON_DOMAINS.forEach((known) => {
    const distance = levenshtein(domain, known);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = known;
    }
  });

  // Short domains: allow 1–2 edits; longer (e.g. hotmail.com.ar): up to 3
  const threshold = domain.length <= 12 ? 2 : 3;
  if (best && bestDistance > 0 && bestDistance <= threshold) {
    return best;
  }

  return null;
};

const detectEmailTypo = (email: string): string | null => {
  const trimmed = email.trim().toLowerCase();
  const atIndex = trimmed.lastIndexOf('@');
  if (atIndex < 1) {
    return null;
  }

  const local = trimmed.slice(0, atIndex);
  const domain = trimmed.slice(atIndex + 1);
  if (!local || !domain.includes('.')) {
    return null;
  }

  const suggestedDomain = suggestDomain(domain);
  if (!suggestedDomain) {
    return null;
  }

  return `${local}@${suggestedDomain}`;
};

export default detectEmailTypo;
